import { ThemeEvents, VariantUpdateEvent, QuantityPackageDiscountEvent, QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 * 3. Quantity package discounts (dynamic price calculation)
 */
class ProductPrice extends HTMLElement {
  /**
   * Original variant price (in cents)
   * @type {number}
   */
  originalPrice = 0;

  /**
   * Original compare at price (in cents)
   * @type {number | null}
   */
  originalCompareAtPrice = null;

  /**
   * Current quantity
   * @type {number}
   */
  currentQuantity = 1;

  /**
   * Current discount percentage
   * @type {number}
   */
  currentDiscount = 0;

  /**
   * Current discount badge text
   * @type {string}
   */
  currentBadgeText = '';

  /**
   * Current badge color scheme
   * @type {string}
   */
  currentBadgeColorScheme = '';

  /**
   * Currency formatter
   * @type {Intl.NumberFormat | null}
   */
  currencyFormatter = null;

  /**
   * Whether price updates are enabled (to avoid conflicts)
   * @type {boolean}
   */
  priceUpdatesEnabled = true;
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    
    // Set up currency formatter and extract original prices
    this.#setupCurrencyFormatter();
    this.#extractOriginalPrices();
    
    // Listen for quantity package discount events
    document.addEventListener(ThemeEvents.quantityPackageDiscount, this.#handleDiscountUpdate);
    
    // Listen for quantity selector updates to track quantity
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    document.removeEventListener(ThemeEvents.quantityPackageDiscount, this.#handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      // Temporarily disable price updates to avoid conflicts
      this.priceUpdatesEnabled = false;
      currentPrice.replaceWith(newPrice);
      
      // Extract new original prices after variant change
      this.#extractOriginalPrices();
      
      // Re-enable and recalculate with current discount
      this.priceUpdatesEnabled = true;
      if (this.currentDiscount > 0) {
        this.#updatePriceDisplay();
      }
    }
  };

  /**
   * Sets up the currency formatter based on Shopify settings.
   * Uses Shopify's locale and country for proper regional formatting.
   */
  #setupCurrencyFormatter() {
    // Get locale from Shopify (e.g., 'fr', 'en', 'de')
    const shopifyLocale = window.Shopify?.locale || 'en';
    // Get country from Shopify (e.g., 'FR', 'US', 'DE')
    const shopifyCountry = window.Shopify?.country || 'US';
    // Combine to get proper locale string (e.g., 'fr-FR', 'en-US')
    const locale = `${shopifyLocale}-${shopifyCountry}`;
    
    // Get active currency from Shopify
    const currency = window.Shopify?.currency?.active || 'USD';
    
    this.currencyFormatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  /**
   * Extracts and stores original prices from the DOM.
   */
  #extractOriginalPrices() {
    const priceElement = this.querySelector('[ref="priceContainer"] .price');
    const compareAtPriceElement = this.querySelector('[ref="priceContainer"] .compare-at-price');

    if (priceElement) {
      const priceText = priceElement.textContent?.trim() || '';
      this.originalPrice = this.#parsePriceToNumber(priceText);
    }

    if (compareAtPriceElement) {
      const compareText = compareAtPriceElement.textContent?.trim() || '';
      this.originalCompareAtPrice = this.#parsePriceToNumber(compareText);
    }
  }

  /**
   * Parses a price string to a number.
   * Handles various currency formats.
   * @param {string} priceText
   * @returns {number}
   */
  #parsePriceToNumber(priceText) {
    const cleaned = priceText.replace(/[^\d.,]/g, '');
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    
    let numberStr = cleaned;
    
    if (lastComma > lastDot) {
      numberStr = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      numberStr = cleaned.replace(/,/g, '');
    }
    
    const number = parseFloat(numberStr);
    return isNaN(number) ? 0 : number;
  }

  /**
   * Rounds a price to 2 decimal places to avoid floating-point precision issues.
   * Example: 4.165 becomes 4.17, not 4.165
   * @param {number} price - Price as a decimal number
   * @returns {number}
   */
  #roundPrice(price) {
    return Math.round(price * 100) / 100;
  }

  /**
   * Formats a number to currency string using Shopify's locale.
   * @param {number} price - Price as a decimal number
   * @returns {string}
   */
  #formatPrice(price) {
    // Round to 2 decimal places before formatting to avoid precision issues
    const roundedPrice = this.#roundPrice(price);
    
    if (!this.currencyFormatter) {
      // Fallback if formatter not set up
      return roundedPrice.toFixed(2);
    }
    
    return this.currencyFormatter.format(roundedPrice);
  }

  /**
   * Handles quantity package discount events.
   * @param {CustomEvent} event
   */
  #handleDiscountUpdate = (event) => {
    if (!(event instanceof QuantityPackageDiscountEvent)) return;
    if (!this.priceUpdatesEnabled) return;

    // Only update if this is for our product
    if (event.detail.productId !== this.dataset.productId) return;

    const { quantity, discountPercentage, badgeText, badgeColorScheme } = event.detail;
    
    this.currentQuantity = quantity;
    this.currentDiscount = discountPercentage;
    this.currentBadgeText = badgeText || '';
    this.currentBadgeColorScheme = badgeColorScheme || '';

    this.#updatePriceDisplay();
  };

  /**
   * Handles quantity selector update events.
   * @param {CustomEvent} event
   */
  #handleQuantityUpdate = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent)) return;
    if (!this.priceUpdatesEnabled) return;

    const quantity = event.detail.quantity;
    if (typeof quantity === 'number' && !isNaN(quantity)) {
      this.currentQuantity = quantity;
      // Recalculate with current discount
      this.#updatePriceDisplay();
    }
  };

  /**
   * Updates the price display with discount applied.
   */
  #updatePriceDisplay() {
    if (!this.priceUpdatesEnabled) return;
    
    const priceContainer = this.querySelector('[ref="priceContainer"]');
    if (!priceContainer) return;

    const priceElement = priceContainer.querySelector('.price');
    if (!priceElement || this.originalPrice === 0) return;

    // Calculate discounted price with proper rounding
    const discountMultiplier = 1 - (this.currentDiscount / 100);
    const discountedUnitPrice = this.#roundPrice(this.originalPrice * discountMultiplier);
    const totalPrice = this.#roundPrice(discountedUnitPrice * this.currentQuantity);

    // Get or create compare at price element
    let compareAtPriceElement = priceContainer.querySelector('.compare-at-price');
    
    if (this.currentDiscount > 0) {
      // Show discounted price
      priceElement.textContent = this.#formatPrice(totalPrice);
      
      // Calculate original total for comparison
      const originalTotal = this.originalPrice * this.currentQuantity;
      
      // If there's no compare at price element but we have a discount, create one
      if (!compareAtPriceElement) {
        const wrapper = document.createElement('span');
        wrapper.setAttribute('role', 'group');
        
        const visuallyHidden = document.createElement('span');
        visuallyHidden.className = 'visually-hidden';
        visuallyHidden.textContent = 'Regular price ';
        
        compareAtPriceElement = document.createElement('span');
        compareAtPriceElement.className = 'compare-at-price';
        compareAtPriceElement.textContent = this.#formatPrice(originalTotal);
        
        wrapper.appendChild(visuallyHidden);
        wrapper.appendChild(compareAtPriceElement);
        
        // Insert before the price element's parent (to maintain proper order)
        const priceParent = priceElement.parentElement;
        if (priceParent && priceParent.getAttribute('role') === 'group') {
          priceParent.parentElement?.insertBefore(wrapper, priceParent);
        } else {
          priceElement.parentElement?.insertBefore(wrapper, priceElement);
        }
        
        // Ensure price element is in a sale price group
        if (!priceParent || priceParent.getAttribute('role') !== 'group') {
          const saleGroup = document.createElement('span');
          saleGroup.setAttribute('role', 'group');
          
          const saleLabel = document.createElement('span');
          saleLabel.className = 'visually-hidden';
          saleLabel.textContent = 'Sale price ';
          
          saleGroup.appendChild(saleLabel);
          priceElement.parentNode?.insertBefore(saleGroup, priceElement);
          saleGroup.appendChild(priceElement);
        }
      } else {
        // Update existing compare at price
        compareAtPriceElement.textContent = this.#formatPrice(originalTotal);
      }

      // Add or update discount badge if badge text is provided
      this.#updateDiscountBadge(priceContainer);
    } else {
      // No discount - show regular price * quantity
      priceElement.textContent = this.#formatPrice(this.originalPrice * this.currentQuantity);
      
      // Remove discount-related elements if they were added
      if (compareAtPriceElement && !this.originalCompareAtPrice) {
        const wrapper = compareAtPriceElement.parentElement;
        if (wrapper?.getAttribute('role') === 'group') {
          wrapper.remove();
        } else {
          compareAtPriceElement.remove();
        }
      }

      // Still show badge even without discount if badge text exists
      this.#updateDiscountBadge(priceContainer);
    }
  }

  /**
   * Updates or creates the discount badge next to the price.
   * @param {Element} priceContainer
   */
  #updateDiscountBadge(priceContainer) {
    if (!this.currentBadgeText) {
      this.#removeDiscountBadge(priceContainer);
      return;
    }

    let badge = priceContainer.querySelector('.discount-badge');
    
    if (!badge) {
      // Create new badge
      badge = document.createElement('span');
      badge.className = 'discount-badge';
      badge.setAttribute('aria-label', 'Discount');
      
      // Insert after the price element
      const priceElement = priceContainer.querySelector('.price');
      if (priceElement && priceElement.parentElement) {
        // Insert after the price's parent group
        const priceGroup = priceElement.closest('[role="group"]') || priceElement;
        priceGroup.parentElement?.insertBefore(badge, priceGroup.nextSibling);
      }
    }
    
    // Update badge text
    badge.textContent = this.currentBadgeText;
    badge.classList.add(`color-${this.currentBadgeColorScheme}`);
  }

  /**
   * Removes the discount badge from the price container.
   * @param {Element} priceContainer
   */
  #removeDiscountBadge(priceContainer) {
    const badge = priceContainer.querySelector('.discount-badge');
    if (badge) {
      badge.remove();
    }
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
